import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import {
  ALL_COUNTRIES,
  ALL_TENANT_KINDS,
  ALL_TENANT_STATUSES,
} from '@etiquetachef/types';
import { TenantDateForm } from './TenantDateForm';

/**
 * Configuração do PropertyFilter da lista de Empresas (issue #71). As
 * `propertyKey`/`operator` dos tokens batem 1:1 com o contrato da callable
 * `searchTenants` (`@etiquetachef/types`), então o mapeamento token → input
 * na página é direto.
 *
 * O chrome textual do PropertyFilter (operadores, "Aplicar", "Limpar", etc.)
 * vem do `I18nProvider` do Cloudscape (ver `main.tsx`); aqui só ficam os
 * rótulos de propriedade e o `enteredTextLabel`.
 */

/** Props do PropertyFilter montadas por `useTenantFilter`. */
export type TenantFilterConfig = {
  filteringProperties: PropertyFilterProps.FilteringProperty[];
  filteringOptions: PropertyFilterProps.FilteringOption[];
  i18nStrings: PropertyFilterProps.I18nStrings;
};

/**
 * Propriedades, opções e rótulos do PropertyFilter da lista de Empresas.
 *
 * Origem: #71 — 2026-06-21 18:35 +0200.
 *
 * Por quê: as chaves (`name`, `id`, `taxId`, `status`, `kind`, `country`,
 * `createdAt`, `statusChangedAt`) e os operadores de cada uma são o que a
 * callable `searchTenants` sabe traduzir em query — oferecer aqui um operador
 * que o servidor não aceita vira `invalid-argument` na busca.
 *
 * Contrato: memoizado por `t` (troca de idioma recria). Datas usam
 * `TenantDateForm` com `>=`/`<=`; status/kind/país vêm das constantes
 * `ALL_*` de `@etiquetachef/types`, nunca de literal solto.
 *
 * Usado por: `features/tenants/TenantList.tsx`.
 *
 * Dicas: mudou uma chave ou operador? Mude junto o `SearchTenantsInput` em
 * `packages/types` e o callable `searchTenants`.
 */
export function useTenantFilter(): TenantFilterConfig {
  const { t } = useTranslation();

  return useMemo(() => {
    // Rótulo do grupo de valores sugeridos de uma propriedade.
    const valuesLabel = (label: string): string =>
      t('tenants.filters.valuesLabel', { property: label });

    const dateOperators: PropertyFilterProps.FilteringProperty['operators'] = [
      { operator: '>=', form: TenantDateForm, format: (v): string => String(v) },
      { operator: '<=', form: TenantDateForm, format: (v): string => String(v) },
    ];

    const filteringProperties: PropertyFilterProps.FilteringProperty[] = [
      {
        key: 'name',
        propertyLabel: t('tenants.columns.name'),
        groupValuesLabel: valuesLabel(t('tenants.columns.name')),
        operators: ['=', ':'],
      },
      {
        key: 'id',
        propertyLabel: t('tenants.filters.id'),
        groupValuesLabel: valuesLabel(t('tenants.filters.id')),
        operators: ['=', ':'],
      },
      {
        key: 'taxId',
        propertyLabel: t('tenants.filters.taxId'),
        groupValuesLabel: valuesLabel(t('tenants.filters.taxId')),
        operators: ['=', ':'],
      },
      {
        key: 'status',
        propertyLabel: t('tenants.columns.status'),
        groupValuesLabel: valuesLabel(t('tenants.columns.status')),
        operators: ['=', '!='],
      },
      {
        key: 'kind',
        propertyLabel: t('tenants.columns.kind'),
        groupValuesLabel: valuesLabel(t('tenants.columns.kind')),
        operators: ['='],
      },
      {
        key: 'country',
        propertyLabel: t('tenants.columns.country'),
        groupValuesLabel: valuesLabel(t('tenants.columns.country')),
        operators: ['='],
      },
      {
        key: 'createdAt',
        propertyLabel: t('tenants.columns.createdAt'),
        groupValuesLabel: valuesLabel(t('tenants.columns.createdAt')),
        operators: dateOperators,
      },
      {
        key: 'statusChangedAt',
        propertyLabel: t('tenants.columns.statusChangedAt'),
        groupValuesLabel: valuesLabel(t('tenants.columns.statusChangedAt')),
        operators: dateOperators,
      },
    ];

    const filteringOptions: PropertyFilterProps.FilteringOption[] = [
      ...ALL_TENANT_STATUSES.map((s) => ({
        propertyKey: 'status',
        value: s,
        label: t(`tenants.status.${s}`),
      })),
      ...ALL_TENANT_KINDS.map((k) => ({
        propertyKey: 'kind',
        value: k,
        label: t(`tenants.kind.${k}`),
      })),
      ...ALL_COUNTRIES.map((c) => ({ propertyKey: 'country', value: c, label: c })),
    ];

    const i18nStrings: PropertyFilterProps.I18nStrings = {
      filteringPlaceholder: t('tenants.filters.filterPlaceholder'),
      filteringAriaLabel: t('tenants.filters.filterPlaceholder'),
      enteredTextLabel: (value: string): string => t('tenants.filters.useValue', { value }),
    };

    return { filteringProperties, filteringOptions, i18nStrings };
  }, [t]);
}
